import { useController, useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";
import React from "react";
import { MandatoryIndicator } from "./MandatoryIndicator";
import { TooltipInfo } from "./TooltipInfo";

type Props = {
  label?: string;
  name: string;
  disabled?: boolean;
  required?: boolean;
  tooltip?: string;
  validation?: Record<string, any>;
  labelClassName?: string;
  defaultValue?: boolean;
  className?: string;
  isBold?: boolean;
};

export const CheckboxFormProvider = ({
  label,
  name,
  disabled = false,
  required = false,
  tooltip,
  validation,
  labelClassName,
  defaultValue = false,
  className,
  isBold = false,
}: Props) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const {
    field: { onChange, value, ref },
  } = useController({
    name,
    control,
    rules: validation,
    defaultValue,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked);
  };

  const errorMessage = errors[name]?.message as string;

  if (label === undefined) {
    return (
      <input
        id={name.toString()}
        type="checkbox"
        onChange={handleChange}
        checked={!!value}
        ref={ref}
        className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-400 disabled:text-gray-300"
        disabled={disabled}
      />
    );
  }

  return (
    <div className={className}>
      <fieldset>
        <label
          className={twMerge(
            "flex cursor-pointer items-center",
            disabled && "cursor-auto",
          )}
        >
          <input
            id={name.toString()}
            type="checkbox"
            onChange={handleChange}
            checked={!!value}
            ref={ref}
            className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-400 disabled:text-gray-300"
            disabled={disabled}
          />
          <span className="flex items-center">
            <span
              className={twMerge(
                "ml-2 select-none text-sm font-normal text-gray-900 disabled:text-gray-400",
                isBold && "font-medium",
                labelClassName,
              )}
            >
              {label}
            </span>
            {required && <MandatoryIndicator />}
            {!!tooltip && <TooltipInfo id={tooltip} text={tooltip} />}
          </span>
        </label>
      </fieldset>
      {!!errorMessage && (
        <p className="mt-1 text-sm text-red-600">{errorMessage}</p>
      )}
    </div>
  );
};
